import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  getActiveSearchAndGroupedClientsList,
  getRetainedSearchAndGroupedClientsList,
  getInactiveSearchAndGroupedClientsList,
  getDeclinedSearchAndGroupedClientsList,
  getPendingSearchAndGroupedClientsList,
  getConnectionTypeSelector,
  getActiveSearchAndGroupedTeamList,
  getPendingSearchAndGroupedTeamList,
  getInactiveSearchAndGroupedTeamList,
  getActiveSearchAndGroupedCollaboratorsList,
  getPendingSearchAndGroupedCollaboratorsList,
  getDeclinedSearchAndGroupedCollaboratorsList,
  getConnectionsSearchValue,
  getAllSearchAndGroupedClientsList,
  getAllSearchAndGroupedCollaboratorsList,
  getAllSearchAndGroupedTeamList,
  getClientConnectionsFilter,
} from 'store/selectors/app';
import ListItem from '../ListItem';
import { AGENT_CONNECTIONS } from 'settings/constants/drawers';
import { TransactionUserRoleMap } from 'settings/constants/transaction';
import styles from './styles.module.scss';
import classNames from 'classnames';
import NoConnections from '../NoConnections';
import { getUserId } from 'store/selectors/user';

const ClientsList = ({ tabLabel }) => {
  const useInternalSorting = true;
  const connectionType = useSelector(getConnectionTypeSelector);
  const searchValue = useSelector(getConnectionsSearchValue);
  const clientFilters = useSelector(getClientConnectionsFilter);
  const currentUserId = useSelector(getUserId);
  const connectionsList = useSelector(
    searchValue && !useInternalSorting
      ? ClientsList.CONNECTION_SELECTORS[connectionType].Aggregated
      : ClientsList.CONNECTION_SELECTORS[connectionType][tabLabel],
  );
  const hideCount =
    clientFilters.selectedTile !== 'All Clients' ||
    clientFilters.tags.length ||
    clientFilters.agentId;
  const sortedSectionNames = useMemo(() => Object.keys(connectionsList).sort(), [connectionsList]);

  const getConnectionRole = (role) =>
    connectionType === AGENT_CONNECTIONS.Collaborators ? TransactionUserRoleMap[role] : role;

  const connectionsCount = connectionsList
    ? Object.values(connectionsList).reduce((sum, arr) => sum + arr.length, 0)
    : 0;

  const getFilteredConnectionsListByLetter = (letter) => {
    const byLetterList = connectionsList[letter];

    if (connectionType !== AGENT_CONNECTIONS.Clients) return byLetterList;

    let filteredList = [];
    if (clientFilters.selectedTile === 'All Clients') filteredList = byLetterList;
    else if (clientFilters.selectedTile === 'My Clients')
      filteredList = byLetterList.filter((client) => client.agentIds?.includes(currentUserId));
    else if (clientFilters.selectedTile === 'Agent' && clientFilters.agentId)
      filteredList = byLetterList.filter((client) =>
        client.agentIds?.includes(clientFilters.agentId),
      );

    if (!clientFilters.tags.length) return filteredList;

    const filterTagsIds = clientFilters.tags.map((tag) => tag.value);

    filteredList = byLetterList.filter((client) =>
      client.clientTags?.some((obj) => filterTagsIds.includes(obj.Id)),
    );

    return filteredList;
  };

  return (
    <div
      className={classNames(styles.listContent, {
        [styles.listContentFull]: !sortedSectionNames.length || !connectionsCount,
      })}
    >
      {sortedSectionNames.length > 0 ? (
        <>
          <div className={styles.count}>
            {hideCount
              ? 'Filtered Results'
              : `${
                  tabLabel === 'Active' && connectionType === AGENT_CONNECTIONS.Team
                    ? connectionsCount - 1
                    : connectionsCount
                } ${connectionType === AGENT_CONNECTIONS.Team ? 'Members' : connectionType}`}
          </div>
          {sortedSectionNames.map((letter, idx) => (
            <div key={idx}>
              {getFilteredConnectionsListByLetter(letter).map(
                ({
                  name,
                  role,
                  teamRole,
                  sendDate,
                  sendStatus,
                  id,
                  searchInstances,
                  transactionInstances,
                  listType,
                  avatarUrl,
                  inviteLink,
                  clientTags,
                  IsManagedByCurrentUser,
                  sendInvitation,
                }) => {
                  if (connectionType === AGENT_CONNECTIONS.Team && id === currentUserId)
                    return null;

                  return (
                    <ListItem
                      connectionName={name}
                      connectionRole={getConnectionRole(role)}
                      teamRole={teamRole}
                      activeTabName={tabLabel}
                      sendDate={sendDate}
                      sendStatus={sendStatus}
                      key={id}
                      connectionId={id}
                      searchInstances={searchInstances}
                      transactionInstances={transactionInstances}
                      listType={listType}
                      avatarUrl={avatarUrl}
                      inviteLink={inviteLink}
                      clientTags={clientTags}
                      IsManagedByCurrentUser={IsManagedByCurrentUser}
                      sendInvitation={sendInvitation}
                      testid="connection_item"
                    />
                  );
                },
              )}
            </div>
          ))}
        </>
      ) : null}
      <NoConnections
        hasSearchQuery={!!searchValue}
        hasResults={!!sortedSectionNames?.length}
        tabLabel={tabLabel}
      />
    </div>
  );
};

ClientsList.CONNECTION_SELECTORS = {
  [AGENT_CONNECTIONS.Clients]: {
    Active: getActiveSearchAndGroupedClientsList,
    Retention: getRetainedSearchAndGroupedClientsList,
    Pending: getPendingSearchAndGroupedClientsList,
    Declined: getDeclinedSearchAndGroupedClientsList,
    Inactive: getInactiveSearchAndGroupedClientsList,
    Aggregated: getAllSearchAndGroupedClientsList,
  },
  [AGENT_CONNECTIONS.Collaborators]: {
    Active: getActiveSearchAndGroupedCollaboratorsList,
    Pending: getPendingSearchAndGroupedCollaboratorsList,
    Declined: getPendingSearchAndGroupedCollaboratorsList,
    Inactive: getDeclinedSearchAndGroupedCollaboratorsList,
    Aggregated: getAllSearchAndGroupedCollaboratorsList,
  },
  [AGENT_CONNECTIONS.Team]: {
    Active: getActiveSearchAndGroupedTeamList,
    Pending: getPendingSearchAndGroupedTeamList,
    Inactive: getInactiveSearchAndGroupedTeamList,
    Aggregated: getAllSearchAndGroupedTeamList,
  },
};

ClientsList.propTypes = {
  tabLabel: PropTypes.string,
};

ClientsList.defaultProps = {
  tabLabel: '',
};

export default ClientsList;
